import { type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/cn';
import { cva } from 'class-variance-authority';

export const badgeBorderColorMap: Record<keyof typeof badgeVariants, string> = {
  default: 'border-primary-foreground',
  gray: 'border-[#FAFAFA]',
  'light-gray': 'border-[#999999]',
  green: 'border-success',
  yellow: 'border-warning',
  blue: 'border-info',
  purple: 'border-highlight',
  orange: 'border-[#B44C24]',
  cyan: 'border-[#2D5D73]',
  destructive: 'border-destructive',
};

export const badgeBackgroundColorMap: Record<
  NonNullable<IBadgeVariants>,
  string
> = {
  default: 'bg-primary',
  gray: 'bg-[#FAFAFA]',
  'light-gray': 'bg-[#999999]',
  green: 'bg-success-subtle',
  yellow: 'bg-warning-subtle',
  blue: 'bg-info-subtle',
  purple: 'bg-highlight-subtle',
  orange: 'bg-[#B44C24]',
  cyan: 'bg-[#2D5D73]',
  destructive: 'bg-destructive',
  outline: '',
};

export type IBadgeVariants =
  | 'default'
  | 'gray'
  | 'light-gray'
  | 'green'
  | 'yellow'
  | 'blue'
  | 'purple'
  | 'orange'
  | 'cyan'
  | 'destructive'
  | 'outline'
  | null
  | undefined;

export type BadgeVariant = VariantProps<typeof badgeVariants>;

const badgeVariants = cva(
  'inline-flex items-center rounded-sm border px-2.5 py-1 text-xs leading-none font-semibold whitespace-nowrap transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        gray: 'border-transparent bg-[#888888] text-[#FFFFFF] dark:bg-[#444444] dark:text-[#F1F1F1]',
        'light-gray':
          'border-transparent bg-[#E5E5E5] text-[#5A5A5A] dark:bg-[#888888] dark:text-[#E5E5E5]',
        green:
          'border-transparent bg-success-subtle text-success dark:bg-success dark:text-success-subtle',
        yellow:
          'border-transparent bg-warning-subtle text-warning dark:bg-warning dark:text-warning-subtle',
        blue: 'border-transparent bg-info-subtle text-info dark:bg-info dark:text-info-subtle',
        purple:
          'border-transparent bg-highlight-subtle text-highlight dark:bg-highlight dark:text-highlight-subtle',
        orange:
          'border-transparent bg-[#FCEED8] text-[#B44C24] dark:bg-[#B44C24] dark:text-[#FCEED8]',
        cyan: 'border-transparent bg-[#D7F9FD] text-[#2D5D73] dark:bg-[#2D5D73] dark:text-[#D7F9FD]',
        destructive:
          'border-transparent bg-destructive-subtle text-destructive dark:bg-destructive dark:text-destructive-subtle',
        outline: 'text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
